import {forgetLearningMe, LearnApi} from '../../api/learnApi';
import {isOk} from '../../utils/apiResponse';
import {LocalStorage} from '../../utils/LocalStorage';
import {onOnboardingProgress} from '../../utils/onboardingProgress';
import {hasOutsidePending, normalizeLearning} from './state/learnModel';
import {ownerKey, rememberEnabled, takeCelebrations} from './state/learnMemory';

/**
 * El estado de «Aprende Dashbo» que comparten la entrada del menú, el lanzador del riel, la página y
 * el paso de la IA del túnel.
 *
 * Vive en memoria de módulo y no en un `useState` porque el marco 2.1 REMONTA en cada navegación
 * (`AppChromeV2`): con el estado en el componente, cada pantalla arrancaba sin datos y la entrada
 * del menú parpadeaba. Y es uno solo porque una escritura de la página («Omitir», el perfil) tiene
 * que mover también el «4/6» del menú y el lanzador, sin otro pedido.
 *
 * Lo mismo que aprendió la tarjeta del drawer (C-panel-01): un solo pedido en vuelo; uno que llega
 * con otro en vuelo marca «pendiente de repetir», la respuesta en vuelo se descarta —puede ser
 * anterior al gesto— y se pregunta UNA vez más, fresco.
 */

// Lo justo para leer «¡Listo! …» (el mismo tiempo que la tarjeta del drawer).
export const CELEBRATION_MS = 4500;

export const STATUS_IDLE = 'idle';
export const STATUS_LOADING = 'loading';
export const STATUS_READY = 'ready';
export const STATUS_ERROR = 'error';

// `answered` cuenta las respuestas que volvieron, bien o mal: la página, al volver de una guía, espera
// la PRIMERA que llega después de montarse para decidir qué tarea abre (la copia que había puede ser de
// antes de que la guía tildara la tarea). Una que falló también cuenta: si no, la página esperaba para
// siempre y no abría ninguna tarea (review C-rp-fe-aprende-04).
function emptyState(owner) {
    return {owner, model: null, status: STATUS_IDLE, celebrating: null, pageOpen: false, answered: 0};
}

let state = emptyState(null);
const listeners = new Set();
// El GET en vuelo (un objeto por pedido, para que la vuelta de uno viejo no libere al nuevo).
let inFlight = null;
let askAgain = false;
// Cuántas escrituras SALIERON. Un GET que salió antes que la última trae un estado más viejo, y la
// respuesta de una escritura que no es la última también: la de la última es la que manda.
let writes = 0;
let celebrationTimer = null;
let unbindProgress = null;
let openPages = 0;

function currentOwner() {
    return ownerKey(LocalStorage.fetchUserId(), LocalStorage.fetchAgencyId());
}

function emit(patch) {
    state = {...state, ...patch};
    listeners.forEach((listener) => listener(state));
}

/** Otra persona u otra agencia en la misma pestaña (el login es SPA): nada de lo anterior le sirve. */
function ensureOwner() {
    const owner = currentOwner();
    if (state.owner === owner) return;
    clearTimeout(celebrationTimer);
    // El pedido en vuelo es de la sesión anterior: la nueva pide el suyo (y la vuelta del viejo se
    // descarta en `receive`).
    inFlight = null;
    askAgain = false;
    state = {...emptyState(owner), pageOpen: openPages > 0};
}

export function getLearningState() {
    ensureOwner();
    return state;
}

function celebrate(keys) {
    clearTimeout(celebrationTimer);
    emit({celebrating: keys});
    celebrationTimer = setTimeout(() => emit({celebrating: null}), CELEBRATION_MS);
}

/**
 * Guarda una respuesta de `learning/me/` (o de una escritura, que devuelve lo mismo). Un error con
 * datos ya cargados no toca nada: es un refresco de fondo y se calla (rule `custom-components`).
 */
function receive(response, owner) {
    // Se mira el dueño de AHORA, no el último que se anotó: el login y el logout son SPA, y una
    // respuesta de la cuenta A no puede escribir en el store ni en la memoria de la cuenta B.
    ensureOwner();
    if (state.owner !== owner) return false;
    if (!isOk(response) || !response.data) {
        emit(state.model ? {answered: state.answered + 1} : {status: STATUS_ERROR, answered: state.answered + 1});
        return false;
    }
    const model = normalizeLearning(response.data);
    rememberEnabled(owner, model.enabled);
    const fresh = model.enabled ? takeCelebrations(owner, model) : [];
    emit({model, status: STATUS_READY, answered: state.answered + 1});
    if (fresh.length) celebrate(fresh);
    return true;
}

/** Vuelve a preguntar. `fresh` saltea la copia de 30 s del `APIClient`. */
export function refreshLearning({fresh = false} = {}) {
    ensureOwner();
    if (!state.owner) return;
    if (inFlight) {
        // Un pedido con cache que llega con otro en vuelo no agrega nada: la entrada del menú y el
        // lanzador se montan juntos en cada navegación, y sin esto cada pantalla pagaba un GET fresco.
        if (fresh) askAgain = true;
        return;
    }
    const request = {};
    inFlight = request;
    const owner = state.owner;
    const startedAt = writes;
    if (!state.model && state.status !== STATUS_LOADING) emit({status: STATUS_LOADING});
    const settle = (response) => {
        if (inFlight !== request) return;
        inFlight = null;
        if (askAgain) {
            askAgain = false;
            refreshLearning({fresh: true});
            return;
        }
        // Una escritura salió mientras este GET viajaba: lo que trae la escritura es más nuevo.
        if (startedAt !== writes) return;
        // `cache: false` saltea la copia del `APIClient` pero no la reescribe: sin olvidarla, la
        // próxima navegación (el marco pide con cache) devolvía el estado de antes de este pedido.
        if (fresh && isOk(response)) forgetLearningMe();
        receive(response, owner);
    };
    LearnApi.getMe({cache: !fresh}).then(settle, () => settle(null));
}

export const WRITE_APPLIED = 'applied';
export const WRITE_FAILED = 'failed';
export const WRITE_STALE = 'stale';

/**
 * Anotar una escritura ANTES de mandarla: quién la hizo (el dueño) y en qué orden salió. Lo que
 * devuelve va después a `applyLearningResponse`.
 */
export function beginLearningWrite() {
    ensureOwner();
    writes += 1;
    return {owner: state.owner, seq: writes};
}

/**
 * Lo que contestó una escritura (`skip`, `complete`, el perfil…): el mismo `data` que el GET.
 * - `applied`: se aplicó. Olvida la copia cacheada, o el próximo pedido con cache volvería a mostrar
 *   el estado anterior al gesto.
 * - `failed`: el backend no la guardó; quien llamó deshace lo que pintó y avisa.
 * - `stale`: no se aplica, en silencio. O cambió la sesión mientras viajaba (lo de la cuenta A no
 *   puede quedar en la B), o salió otra escritura después: su respuesta trae el estado más nuevo, y
 *   aplicar ésta volvería atrás lo que la otra ya pintó.
 */
export function applyLearningResponse(response, ticket) {
    ensureOwner();
    if (!ticket || ticket.owner !== state.owner) return WRITE_STALE;
    if (!isOk(response) || !response.data) return WRITE_FAILED;
    forgetLearningMe();
    if (ticket.seq !== writes) return WRITE_STALE;
    return receive(response, state.owner) ? WRITE_APPLIED : WRITE_STALE;
}

export function subscribeLearning(listener) {
    listeners.add(listener);
    // La app avisa cuando algo pudo tildar una tarea (guardar un informe, crear una línea, cargar
    // memoria: `notifyOnboardingProgress`). Una persona sin la página no tiene nada que tildar.
    if (!unbindProgress) {
        const unbindApp = onOnboardingProgress(() => {
            if (state.model && !state.model.enabled) return;
            refreshLearning({fresh: true});
        });
        // Conectar la IA y la primera consulta pasan en otra pestaña (ChatGPT, Claude): volver a
        // Dashbo es cuando pudieron tildarse. Con la copia de 30 s: ir y venir no es un GET por vez.
        // La página pide fresco por su cuenta.
        const onFocus = () => {
            if (openPages > 0 || !state.model || !state.model.enabled) return;
            if (hasOutsidePending(state.model)) refreshLearning();
        };
        window.addEventListener('focus', onFocus);
        unbindProgress = () => {
            unbindApp();
            window.removeEventListener('focus', onFocus);
        };
    }
    return () => {
        listeners.delete(listener);
        if (!listeners.size && unbindProgress) {
            unbindProgress();
            unbindProgress = null;
        }
    };
}

/**
 * La página avisa que está abierta: mientras lo esté, el lanzador no anuncia la celebración al
 * lector de pantalla (la anuncia la página; dos regiones diciendo lo mismo se leen dos veces).
 */
export function registerLearnPage() {
    openPages += 1;
    emit({pageOpen: true});
    return () => {
        openPages = Math.max(0, openPages - 1);
        emit({pageOpen: openPages > 0});
    };
}

/** Sólo para los tests: el estado de módulo sobrevive entre casos. */
export function resetLearningStore() {
    clearTimeout(celebrationTimer);
    if (unbindProgress) unbindProgress();
    unbindProgress = null;
    listeners.clear();
    inFlight = null;
    askAgain = false;
    writes = 0;
    openPages = 0;
    state = emptyState(null);
}
